import React, { useState, useEffect, useCallback } from "react";
import { 
  Save, 
  Copy, 
  Zap, 
  Trash2, 
  ExternalLink, 
  Check, 
  AlertCircle, 
  Eye, 
  EyeOff, 
  ShoppingBag,
  Package,
  CreditCard
} from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••••••••••••••";

const ShopifyConfig = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [shopifyConfig, setShopifyConfig] = useState({
    shop_domain: "",
    access_token: "",
    api_key: "",
    api_secret: "",
    order_tracking_enabled: true,
    product_sync_enabled: true,
    draft_orders_enabled: true,
    is_active: false,
  });

  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/shopify/config", {
        aiAgentId: activeAiAgentId
      });
      if (response && response.success && response.config) {
        setShopifyConfig({
          shop_domain: response.config.shop_domain || "",
          access_token: response.config.access_token ? UNCHANGED_KEY : "",
          api_key: response.config.api_key || "",
          api_secret: response.config.api_secret ? UNCHANGED_KEY : "",
          order_tracking_enabled: response.config.order_tracking_enabled !== false,
          product_sync_enabled: response.config.product_sync_enabled !== false,
          draft_orders_enabled: response.config.draft_orders_enabled !== false,
          is_active: Boolean(response.config.is_active),
        });
        setWebhookUrl(response.config.webhook_url || "");
      }
    } catch (error) {
      console.error("Failed to load Shopify config:", error);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const cleanDomain = (domain) => {
    let d = (domain || "").trim().toLowerCase();
    if (d.startsWith("https://")) d = d.slice(8);
    if (d.startsWith("http://")) d = d.slice(7);
    return d.replace(/\/+$/, "");
  };

  const handleDomainChange = (e) => {
    setShopifyConfig(prev => ({
      ...prev,
      shop_domain: e.target.value
    }));
  };

  const saveConfig = async () => {
    const domain = cleanDomain(shopifyConfig.shop_domain);
    if (!domain) {
      showToast("Please enter your Shopify store domain", "error");
      return;
    }

    if (!shopifyConfig.access_token.trim()) {
      showToast("Please enter your Shopify Admin API access token", "error");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...shopifyConfig,
        shop_domain: domain
      };

      const response = await ApiClient.request("/shopify/config", {
        method: "PUT",
        body: JSON.stringify(payload),
        aiAgentId: activeAiAgentId
      });

      if (response && response.success) {
        if (response.webhook_url) {
          setWebhookUrl(response.webhook_url);
        }
        showToast("Shopify configuration saved successfully!", "success");
        if (shopifyConfig.access_token !== UNCHANGED_KEY) {
          setShopifyConfig(prev => ({
            ...prev,
            shop_domain: domain,
            access_token: UNCHANGED_KEY
          }));
        }
        if (shopifyConfig.api_secret && shopifyConfig.api_secret !== UNCHANGED_KEY) {
          setShopifyConfig(prev => ({
            ...prev,
            api_secret: UNCHANGED_KEY
          }));
        }
      }
    } catch (error) {
      // ApiClient handles error toast
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/shopify", {
        method: "DELETE",
        aiAgentId: activeAiAgentId
      });
      if (response && response.success) {
        setShopifyConfig({
          shop_domain: "",
          access_token: "",
          api_key: "",
          api_secret: "",
          order_tracking_enabled: true,
          product_sync_enabled: true,
          draft_orders_enabled: true,
          is_active: false,
        });
        setWebhookUrl("");
        showToast("Shopify integration removed successfully!", "success");
      }
    } catch (error) {
      // Handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const toggleActive = async () => {
    const nextState = !shopifyConfig.is_active;
    if (nextState) {
      if (!shopifyConfig.shop_domain?.trim() || !shopifyConfig.access_token?.trim()) {
        showToast("Please enter and save store domain and access token first", "error");
        return;
      }
      if (shopifyConfig.access_token !== UNCHANGED_KEY) {
        showToast("Please save your configuration changes before activating", "warning");
        return;
      }
    }

    try {
      const response = await ApiClient.request("/shopify/config", {
        method: "PUT",
        body: JSON.stringify({
          ...shopifyConfig,
          is_active: nextState
        }),
        aiAgentId: activeAiAgentId
      });

      if (response && response.success) {
        setShopifyConfig(prev => ({ ...prev, is_active: nextState }));
        showToast(nextState ? "Shopify integration activated!" : "Shopify integration paused", "success");
      } else {
        showToast("Failed to update integration status", "error");
      }
    } catch (error) {
      const msg = error?.message || error?.detail || "Failed to update integration status";
      showToast(msg, "error");
    }
  };

  const toggleCapability = async (key, label) => {
    const nextValue = !shopifyConfig[key];

    const hasCredentials = Boolean(
      shopifyConfig.shop_domain?.trim() && 
      (shopifyConfig.access_token === UNCHANGED_KEY || shopifyConfig.access_token?.trim())
    );

    if (!hasCredentials) {
      showToast(`Please save Shopify credentials first before configuring ${label}`, "error");
      return;
    }

    if (shopifyConfig.access_token !== UNCHANGED_KEY) {
      setShopifyConfig(prev => ({ ...prev, [key]: nextValue }));
      showToast(`${label} ${nextValue ? "enabled" : "disabled"}. Click 'Save Shopify Config' to apply changes.`, "info");
      return;
    }

    try {
      const response = await ApiClient.request("/shopify/config", {
        method: "PUT",
        body: JSON.stringify({
          [key]: nextValue
        }),
        aiAgentId: activeAiAgentId
      });

      if (response && response.success) {
        setShopifyConfig(prev => ({ ...prev, [key]: nextValue }));
        showToast(`${label} ${nextValue ? "enabled" : "disabled"} successfully!`, "success");
      } else {
        showToast(`Failed to update ${label}`, "error");
      }
    } catch (error) {
      const errorMsg = error?.message || error?.detail || `Failed to update ${label}`;
      showToast(errorMsg, "error");
    }
  };

  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    let path = webhookUrl;
    if (webhookUrl.startsWith("http://") || webhookUrl.startsWith("https://")) {
      try {
        const urlObj = new URL(webhookUrl);
        path = urlObj.pathname + urlObj.search;
      } catch (e) {}
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    let cleanPath = path.startsWith("/") ? path : "/" + path;
    if (cleanBase.endsWith("/api") && cleanPath.startsWith("/api")) {
      cleanBase = cleanBase.slice(0, -4);
    }
    return cleanBase + cleanPath;
  };

  const displayUrl = getDisplayWebhookUrl();

  const copyWebhookUrl = async () => {
    if (!displayUrl) return;
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-12 px-6 lg:pt-6 lg:pb-16 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-5xl space-y-8">
        {/* Header */}
        <PageHeader
          title="Shopify Integration"
          description="Empower your AI agent with real-time store knowledge, live order tracking, and product catalog lookups."
          actions={
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-[24px] border border-gray-100 shadow-xl shadow-gray-100/50">
              <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${shopifyConfig.is_active ? "text-[#5E8E3E]" : "text-gray-300"}`}>
                {shopifyConfig.is_active ? "Operational" : "Offline"}
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={shopifyConfig.is_active}
                  onChange={toggleActive}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-[#5E8E3E] transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-[#5E8E3E]"></div>
              </label>
            </div>
          }
        />

        {/* Credentials Form */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-sm space-y-6">
          <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center p-1.5 bg-white border border-gray-100 shadow-sm">
              <img src="/shopify.png" alt="Shopify" className="w-full h-full object-contain" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Shopify Store Credentials</h4>
              <p className="text-xs text-gray-500 mt-0.5">
                Connect your Shopify custom app Admin API access token
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Store Domain */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700">
                Store Domain <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={shopifyConfig.shop_domain}
                  onChange={handleDomainChange}
                  placeholder="your-store-name.myshopify.com"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#95BF47]/40 focus:border-[#95BF47] text-sm text-gray-800 placeholder-gray-400"
                />
              </div>
              <p className="text-[11px] text-gray-400">
                Enter your `.myshopify.com` domain or primary store domain.
              </p>
            </div>

            {/* Admin API Access Token */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700">
                Admin API Access Token <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showToken ? "text" : "password"}
                  value={shopifyConfig.access_token === UNCHANGED_KEY ? MASK_DISPLAY : shopifyConfig.access_token}
                  onChange={(e) => {
                    setShopifyConfig(prev => ({ ...prev, access_token: e.target.value }));
                  }}
                  placeholder="shpat_xxxxxxxxxxxxxxxxxxxxxxxx"
                  className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#95BF47]/40 focus:border-[#95BF47] text-sm text-gray-800 placeholder-gray-400 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[11px] text-gray-400">
                Begins with <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-700">shpat_</code>. Stored securely with Fernet encryption.
              </p>
            </div>

            {/* API Key (Optional) */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700">
                API Key / Client ID <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <input
                type="text"
                value={shopifyConfig.api_key}
                onChange={(e) => setShopifyConfig(prev => ({ ...prev, api_key: e.target.value }))}
                placeholder="Custom App Client ID"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#95BF47]/40 focus:border-[#95BF47] text-sm text-gray-800 placeholder-gray-400"
              />
            </div>

            {/* API Secret Key (Optional) */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700">
                API Secret Key <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <div className="relative">
                <input
                  type={showSecret ? "text" : "password"}
                  value={shopifyConfig.api_secret === UNCHANGED_KEY ? MASK_DISPLAY : shopifyConfig.api_secret}
                  onChange={(e) => setShopifyConfig(prev => ({ ...prev, api_secret: e.target.value }))}
                  placeholder="Custom App Secret / Webhook Secret"
                  className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#95BF47]/40 focus:border-[#95BF47] text-sm text-gray-800 placeholder-gray-400 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
            {shopifyConfig.shop_domain && (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-3 border border-red-200 text-red-600 rounded-2xl hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold text-sm"
              >
                <Trash2 size={18} />
                Delete Integration
              </button>
            )}
            <button
              type="button"
              onClick={saveConfig}
              disabled={saving || !shopifyConfig.shop_domain?.trim() || !shopifyConfig.access_token?.trim()}
              className="flex items-center gap-2 px-8 py-3.5 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-lg shadow-gray-200 text-sm ml-auto"
            >
              <Save size={18} />
              {saving ? "Saving..." : "Save Shopify Config"}
            </button>
          </div>
        </div>

        {/* Capabilities / Feature Toggles */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-sm space-y-6">
          <div className="border-b border-gray-100 pb-4">
            <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#5E8E3E]" />
              Agent Capabilities
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              Select what data the AI agent is allowed to access and retrieve on behalf of customers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Order Tracking */}
            <div className="p-5 rounded-2xl border border-gray-100 bg-[#FBFBFB] flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-blue-50 text-blue-600">
                  <Package className="w-5 h-5" />
                </div>
                <div>
                  <h5 className="text-sm font-bold text-gray-900">Order Lookup & Tracking</h5>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                    Allows the AI agent to look up order status, tracking numbers, and delivery dates using order number or email.
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input
                  type="checkbox"
                  checked={shopifyConfig.order_tracking_enabled}
                  onChange={() => toggleCapability("order_tracking_enabled", "Order Lookup & Tracking")}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#5E8E3E]"></div>
              </label>
            </div>

            {/* Product Sync */}
            <div className="p-5 rounded-2xl border border-gray-100 bg-[#FBFBFB] flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600">
                  <ShoppingBag className="w-5 h-5" />
                </div>
                <div>
                  <h5 className="text-sm font-bold text-gray-900">Product Catalog Sync</h5>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                    Permits the AI agent to answer customer inquiries about product availability, variants, pricing, and specs.
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input
                  type="checkbox"
                  checked={shopifyConfig.product_sync_enabled}
                  onChange={() => toggleCapability("product_sync_enabled", "Product Catalog Sync")}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#5E8E3E]"></div>
              </label>
            </div>

            {/* Direct Checkout & Draft Orders */}
            <div className="p-5 rounded-2xl border border-gray-100 bg-[#FBFBFB] flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-purple-50 text-purple-600">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <h5 className="text-sm font-bold text-gray-900">Direct Checkout & Draft Orders</h5>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                    Permits the AI agent to create draft orders and generate instant, secure Shopify checkout payment links in chat.
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input
                  type="checkbox"
                  checked={shopifyConfig.draft_orders_enabled}
                  onChange={() => toggleCapability("draft_orders_enabled", "Direct Checkout & Draft Orders")}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#5E8E3E]"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Webhook Endpoint Card */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-sm space-y-4">
          <div className="border-b border-gray-100 pb-4">
            <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-[#5E8E3E]" />
              Shopify Webhook Callback
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              Add this webhook in your Shopify Custom App or Notifications settings to receive real-time order and catalog updates.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs font-mono text-gray-700 truncate select-all">
              {displayUrl || "Save configuration to generate webhook URL"}
            </div>
            <button
              onClick={copyWebhookUrl}
              disabled={!displayUrl}
              className="px-5 py-3 rounded-xl bg-gray-900 hover:bg-black text-white text-xs font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              <span>{copied ? "Copied" : "Copy Webhook"}</span>
            </button>
          </div>
        </div>

        {/* Setup Instructions Accordion */}
        <div className="bg-gradient-to-br from-[#FBFDF9] to-[#F3F8ED] rounded-3xl p-6 sm:p-8 border border-[#95BF47]/30 space-y-4">
          <h4 className="text-sm font-bold uppercase tracking-wider text-[#3D6B26] flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            How to configure Shopify Custom App
          </h4>
          <ol className="space-y-3 text-xs text-gray-700 list-decimal list-inside leading-relaxed">
            <li>
              Log in to your <strong>Shopify Admin</strong> &rarr; navigate to <strong>Settings &rarr; Apps and sales channels &rarr; Develop apps</strong>.
            </li>
            <li>
              Click <strong>Create an app</strong> and name it (e.g. <em>EasyChat Assistant</em>).
            </li>
            <li>
              Under <strong>Configuration</strong>, select <strong>Admin API integration</strong> and grant the following permissions:
              <span className="block mt-1 font-mono text-[11px] text-gray-600 bg-white/70 p-2 rounded-lg border border-[#95BF47]/20">
                read_products, read_orders, write_draft_orders, read_draft_orders, read_customers, read_inventory
              </span>
            </li>
            <li>
              Click <strong>Install app</strong> and reveal the <strong>Admin API access token</strong> (begins with <code>shpat_</code>).
            </li>
            <li>
              Paste your store domain and token above, then click <strong>Save Shopify Config</strong>.
            </li>
          </ol>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Disconnect Shopify Integration"
        message="Are you sure you want to disconnect Shopify? The AI agent will no longer be able to track orders or sync product information from this store."
        confirmText="Disconnect Shopify"
        loading={saving}
      />
    </div>
  );
};

export default ShopifyConfig;
